import { useToast } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useRef, useState } from "react"
import { useDocumentWarehouseBootstrap } from "./useDocumentWarehouseBootstrap"

type BaseCreateOptions = {
  preferredWarehouseId?: string
  replace?: boolean
}

type NavigationTarget = {
  to: string
  params?: Record<string, string>
  search?: Record<string, unknown>
  replace?: boolean
}

type UseCreateWarehouseDocumentDraftConfig<
  TPayload,
  TDraft extends { id: string },
  TOptions extends BaseCreateOptions = BaseCreateOptions,
> = {
  mutationKey: readonly unknown[]
  documentCode: string
  blockedWarehouseDescription: string
  missingWarehouseDescription: string
  createDraftRequest: (payload: TPayload, options?: TOptions) => Promise<TDraft>
  buildPayload: (
    warehouseId: string,
    options?: TOptions,
  ) => Promise<TPayload> | TPayload
  getSuccessNavigation: (draft: TDraft, options?: TOptions) => NavigationTarget
}

export function useCreateWarehouseDocumentDraft<
  TPayload,
  TDraft extends { id: string },
  TOptions extends BaseCreateOptions = BaseCreateOptions,
>({
  mutationKey,
  documentCode,
  blockedWarehouseDescription,
  missingWarehouseDescription,
  createDraftRequest,
  buildPayload,
  getSuccessNavigation,
}: UseCreateWarehouseDocumentDraftConfig<TPayload, TDraft, TOptions>) {
  const toast = useToast()
  const navigate = useNavigate()
  const creationInFlight = useRef(false)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const {
    availableWarehouses,
    isPending: warehousesPending,
    refreshAvailability,
  } = useDocumentWarehouseBootstrap()

  const mutation = useMutation({
    mutationKey: [...mutationKey, "draft-init"],
    mutationFn: async (options?: TOptions) => {
      const warehouseId = options?.preferredWarehouseId
      if (!warehouseId) {
        throw new Error(missingWarehouseDescription)
      }
      const payload = await buildPayload(warehouseId, options)
      const draft = await createDraftRequest(payload, options)
      return { draft, options }
    },
    onSuccess: ({ draft, options }) => {
      navigate(getSuccessNavigation(draft, options))
    },
    onError: (error: any) => {
      toast({
        title: `Nie udało się utworzyć szkicu ${documentCode}`,
        description: error?.body?.error ?? error?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    },
  })

  const createDocument = useCallback(
    async (options?: TOptions) => {
      if (warehousesPending) {
        toast({
          status: "info",
          title: "Ładowanie danych magazynów",
          description: "Proszę chwilę poczekać i spróbować ponownie.",
        })
        return false
      }
      if (creationInFlight.current || mutation.isPending) return false

      creationInFlight.current = true
      setIsCheckingAvailability(true)
      try {
        const availability = await refreshAvailability()
        if (!availability.ready) {
          toast({
            status: "error",
            title: "Nie można sprawdzić dostępności magazynu",
            description:
              "Odśwież stronę lub spróbuj ponownie. Szkic nie został utworzony.",
          })
          return false
        }

        if (availability.isBlocked(options?.preferredWarehouseId)) {
          toast({
            status: "warning",
            title: "Inwentaryzacja",
            description: blockedWarehouseDescription,
          })
          return false
        }

        const preferredWarehouseId =
          options?.preferredWarehouseId &&
          availability.availableWarehouses.some(
            (warehouse) => warehouse.id === options.preferredWarehouseId,
          )
            ? options.preferredWarehouseId
            : availability.availableWarehouses[0]?.id
        if (!preferredWarehouseId) {
          toast({
            status: "warning",
            title: "Brak aktywnych magazynów",
            description: missingWarehouseDescription,
          })
          return false
        }

        await mutation.mutateAsync({
          ...options,
          preferredWarehouseId,
        } as TOptions)
        return true
      } catch {
        return false
      } finally {
        creationInFlight.current = false
        setIsCheckingAvailability(false)
      }
    },
    [
      blockedWarehouseDescription,
      missingWarehouseDescription,
      mutation,
      refreshAvailability,
      toast,
      warehousesPending,
    ],
  )

  return {
    createDocument,
    isCreating: isCheckingAvailability || mutation.isPending,
    isResolvingWarehouses: warehousesPending,
    canCreate: availableWarehouses.length > 0,
  }
}
